import React, { useState, useEffect } from "react";
import axios from "axios";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Download } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { FaFileInvoice } from "react-icons/fa";

import "./VehicleCollectionReport.css";
import { fetchUserProfile } from "../redux/slices/branch/userProfileSlice";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const VehicleCollectionReport = () => {
  const dispatch = useDispatch();
  const { profile } = useSelector((state) => state.userProfile);
  const navigate = useNavigate();

  const [centers, setCenters] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedCenter, setSelectedCenter] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("");

  const [collectionData, setCollectionData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const ITEMS_PER_PAGE_OPTIONS = [10, 20, 50, 100];

  useEffect(() => {
    dispatch(fetchUserProfile());
  }, [dispatch]);

  const fetchCollection = async (page = 1) => {
    if (!profile?.branchId) return;

    try {
      setLoading(true);

      const res = await axios.get(
        "https://payyug.in/api//gold-loan/collection-report/all",
        {
          params: {
            superAdminId: profile.superAdminId,
            page,
            limit: itemsPerPage,
          },
        }
      );

      if (res.data?.success) {
        setCollectionData(res.data.data || []);
        setCurrentPage(res.data.pagination.currentPage);
        setTotalPages(res.data.pagination.totalPages);
        setTotalRecords(res.data.pagination.totalRecords);
      }
    } catch (err) {
      console.error("Collection fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCollection(currentPage);
  }, [profile, currentPage, itemsPerPage]);

  const filteredData = collectionData.filter((r) => {
    const text = `
      ${r.loanAccount?.loanAccountNumber}
      ${r.loanAccount?.memberName}
      ${r.loanAccount?.centerName}
      ${r.loanAccount?.groupName}
    `.toLowerCase();

    return text.includes(searchQuery.toLowerCase());
  });

  const handleFilter = () => {
    if (!selectedCenter || !selectedGroup) {
      toast.warn("Please select center and group");
      return;
    }
    setCurrentPage(1);
    fetchCollection(1);
  };

  const exportCSV = () => {
    const csv = collectionData
      .map((r) =>
        [r.memberId, r.groupId, r.emiAmount, r.paidAmount, r.paidDate].join(",")
      )
      .join("\n");

    saveAs(new Blob([csv]), "vehicle_collection_report.csv");
  };

  const exportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(collectionData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Vehicle Collection Report");
    XLSX.writeFile(wb, "vehicle_collection_report.xlsx");
  };

  const exportPDF = () => {
    const doc = new jsPDF("l", "pt", "a4");
    autoTable(doc, {
      head: [["Member Id", "Group Id", "EMI Amount","Paid Amount", "Paid Date", "Status"]],
      body: collectionData.map((r) => [
        r.memberId,
        r.groupId,
        r.emiAmount,
        r.paidAmount,
        r.paidDate ? new Date(r.paidDate).toLocaleDateString() : "-",
        r.status,
      ]),
    });
    doc.save("vehicle_collection_report.pdf");
  };

  return (
    <div className="vehcr-container">
      <h1 className="vehcr-heading">Vehicle Collection Report</h1>

      {filteredData.length > 0 && (
        <div className="vehcr-pdf-download-btns">
          <button onClick={exportCSV}><Download size={15} /> CSV</button>
          <button onClick={exportExcel}><Download size={15} /> Excel</button>
          <button onClick={exportPDF}><Download size={15} /> PDF</button>
        </div>
      )}

      <div className="vehcr-report-filter">
        <input
          placeholder="Search..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setCurrentPage(1);
          }}
        />
        <div>
          <label>Show </label>
          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            {ITEMS_PER_PAGE_OPTIONS.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
            <option value="50000"> ALL</option>
          </select>
          <span> entries</span>
        </div>
      </div>

      <div className="vehcr-report-table-container">
        <table className="vehcr-report-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Member Id</th>
              <th>EMI Amount</th>
              <th>Paid Amount</th>
              <th>Paid Date</th>
              <th>Status</th>
              <th>Receipt</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="8" align="center">Loading...</td>
              </tr>
            ) : filteredData.length ? (
              filteredData.map((r, i) => {
                const nextemi =
                  i < filteredData.length - 1 ? filteredData[i + 1] : null;

                return (
                  <tr key={r.id}>
                    <td>{(currentPage - 1) * itemsPerPage + i + 1}</td>
                    <td>{r.memberId || "N/A"}</td>
                    <td>₹{r.emiAmount || "N/A"}</td>
                    <td>₹{r.paymentAmount || "N/A"}</td>
                    <td>
                      {r.paymentDate
                        ? new Date(r.paymentDate).toLocaleDateString("en-GB")
                        : "-"}
                    </td>

                    <td className={r.status === "PAID" ? "status-paid" : ""}>
                      {r.status || "N/A"}
                    </td>

                    <td
                      className="receipt-cell"
                      onClick={() =>
                        navigate("/VehicleLoan/VehicleLoanCollectionReceipt", {
                          state: { r, nextemi },
                        })
                      }
                    >
                      <FaFileInvoice className="receipt-icon" />
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="8" align="center">No data found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="vehcr-pagination">
        <button
          disabled={currentPage === 1 || filteredData.length === 0}
          onClick={() => setCurrentPage((p) => p - 1)}
        >
          ⬅ Prev
        </button>

        <span>
          {filteredData.length === 0
            ? "No data"
            : `Page ${currentPage} of ${totalPages}`}
        </span>

        <button
          disabled={currentPage === totalPages || filteredData.length === 0}
          onClick={() => setCurrentPage((p) => p + 1)}
        >
          Next ➡
        </button>
      </div>
    </div>
  );
};

export default VehicleCollectionReport;